import { supabase } from "@/lib/supabase";

export interface ReviewPhoto {
  src: string;
  alt: string;
}

export interface ReviewSubmissionInput {
  visitor_name: string;
  role?: string;
  location?: string;
  village?: string;
  experience?: string;
  experience_slug?: string;
  rating: number;
  comment: string;
  visit_date?: string;
  photos?: ReviewPhoto[];
}

/** Public storage bucket and the folder new review photos are written into. */
const REVIEW_BUCKET = "public";
const REVIEW_DIR = "review-photos";

const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "gif", "avif"];
const MAX_KB = 8000;

/** Max number of photos a single visitor can attach. */
export const MAX_REVIEW_PHOTOS = 6;

function safeExtension(file: File): string {
  const parts = file.name.split(".");
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  if (IMAGE_EXT.includes(ext)) return ext;
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/gif") return "gif";
  return "jpg";
}

/**
 * Uploads a single visit photo from the public review form. Filenames generated
 * by phones (uppercase, spaces, symbols) are not storage-safe, so a clean object
 * name is always generated instead of reusing the original one.
 * Returns the stored object path, which the site resolves to a signed URL.
 */
export async function uploadReviewPhoto(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files can be added.");
  }
  if (file.size > MAX_KB * 1024) {
    throw new Error("Photos must be 8 MB or smaller.");
  }

  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 7);
  const objectName = `review-${stamp}-${rand}.${safeExtension(file)}`;
  const path = `${REVIEW_DIR}/${objectName}`;

  const { error } = await supabase.storage.from(REVIEW_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;

  return path;
}

/**
 * Saves a visitor's review to the backend as a pending review. Submitted
 * anonymously, so it is inserted without reading the row back (the public role
 * has insert-only access; the team moderates it in the admin Reviews module).
 */
export async function submitReview(input: ReviewSubmissionInput): Promise<void> {
  const photos = input.photos && input.photos.length > 0 ? input.photos : [];

  const { error } = await supabase.from("reviews").insert({
    visitor_name: input.visitor_name.trim(),
    role: input.role?.trim() || null,
    location: input.location?.trim() || null,
    village: input.village?.trim() || null,
    experience: input.experience?.trim() || null,
    experience_slug: input.experience_slug?.trim() || null,
    rating: input.rating,
    comment: input.comment.trim(),
    visit_date: input.visit_date || null,
    photos,
    status: "pending",
  });
  if (error) throw error;
}